import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { X, ChevronRight, ChevronLeft, Sparkles, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const GUIDE_STEPS = [
  {
    id: "welcome",
    titleBg: "Добре дошли в Nutramilo! 🎉",
    titleEn: "Welcome to Nutramilo! 🎉",
    descBg: "Профилът Ви е готов. Сега ще Ви покажем какво е достъпно в платформата и как да го използвате максимално — отнема около 2 минути.",
    descEn: "Your profile is ready. Let's quickly show you what's available and how to get the most out of the platform — about 2 minutes.",
    icon: "sparkles",
    action: null,
  },
  {
    id: "profile_completeness",
    titleBg: "1. Допълнете профила си",
    titleEn: "1. Complete your profile",
    descBg: "В картата „Точност на профила“ на Dashboard-а виждате процент и следващата най-полезна стъпка. Всяко добавяне повишава точността на препоръките.",
    descEn: "The 'Profile accuracy' card on the dashboard shows a % and the next best step. Every input boosts recommendation accuracy.",
    icon: "user",
    targetTestId: "profile-completeness-card",
    checkFn: (user) => {
      // Treat any body measurements as 'profile started'
      const body = user?.profile?.body;
      return !!(body?.height && body?.weight);
    },
  },
  {
    id: "lab_upload",
    titleBg: "2. Качете лабораторни резултати",
    titleEn: "2. Upload lab results",
    descBg: "Качете PDF с кръвни изследвания в секция 'Лабораторни анализи'. AI извлича стойностите автоматично и отключва вашата Биологична възраст (Levine PhenoAge).",
    descEn: "Upload blood-test PDFs in 'Lab analyses'. AI extracts values automatically and unlocks your Biological Age (Levine PhenoAge).",
    icon: "upload",
    targetTestId: "lab-upload-card",
  },
  {
    id: "meal_log",
    titleBg: "3. Запишете храненето си",
    titleEn: "3. Log your meals",
    descBg: "В Хранителния дневник търсете храна и добавяйте порции. Системата изчислява DIL (Dietary Insulin Load) за всяко хранене. 💡 Можете да влачите храни между закуска/обяд/вечеря.",
    descEn: "In the Meal Log search food and add portions. The system calculates DIL per meal. 💡 Drag & drop between breakfast/lunch/dinner.",
    icon: "utensils",
    targetTestId: "meal-log-card",
  },
  {
    id: "phenotype_card",
    titleBg: "4. Вашият метаболитен модел",
    titleEn: "4. Your metabolic pattern",
    descBg: "Картата „Вашият метаболитен модел“ обединява фенотип, симптоми и лабораторни данни в едно просто обобщение — с „Какво означава“ и „Какво да направиш днес“.",
    descEn: "The 'Your metabolic pattern' card combines phenotype, symptoms and labs into one simple summary — with 'What it means' and 'What to do today'.",
    icon: "sparkles",
    targetTestId: "composite-phenotype-card",
  },
  {
    id: "plate_simulator",
    titleBg: "5. Симулатор на чинията",
    titleEn: "5. Plate Simulator",
    descBg: "Вашата чиния се оцветява веднага според това, което ядете. Вижте разпределението на градивните вещества и меки съвети — без сложни думи.",
    descEn: "Your visual plate color-codes in real-time based on what you eat. See macro distribution and gentle suggestions — no medical jargon.",
    icon: "utensils",
    targetTestId: "plate-simulator-card",
  },
  {
    id: "digital_twin",
    titleBg: "6. Digital Twin · „Ако продължа така?“",
    titleEn: "6. Digital Twin · 'What if I keep going?'",
    descBg: "Digital Twin ви показва какво ще се случи с тялото ви ако продължите да се храните така — на език, който разбирате. С AI препоръки и симулация на добавки.",
    descEn: "Digital Twin shows what will happen to your body if you keep eating this way — in human language. With AI actions and supplement simulation.",
    icon: "sparkles",
    targetTestId: "digital-twin-panel",
  },
  {
    id: "tracking",
    titleBg: "7. Проследявайте ежедневно",
    titleEn: "7. Track daily",
    descBg: "Записвайте вода, сън и активност в „Дневно проследяване“. Тези данни влияят на вашия Endo Score и DIL buffer.",
    descEn: "Log water, sleep and activity in 'Daily Tracking'. These drive your Endo Score and DIL buffer.",
    icon: "activity",
    targetTestId: "daily-tracking-card",
  },
  {
    id: "biological_age",
    titleBg: "8. Изчислете биологичната си възраст",
    titleEn: "8. Calculate biological age",
    descBg: "След качени лабораторни данни панелът „Биологична възраст“ автоматично попълва 9 маркера (Levine PhenoAge) и показва 12-месечна тенденция.",
    descEn: "After uploading labs, the 'Biological Age' panel auto-fills 9 markers (Levine PhenoAge) and shows a 12-month trend.",
    icon: "sparkles",
    targetTestId: "biological-age-panel",
  },
  {
    id: "ai_chat",
    titleBg: "9. Питайте AI специалистите",
    titleEn: "9. Ask AI specialists",
    descBg: "AI Чатът обединява 4 специалиста (EndoBot, NutriBot, NephroBot, HemoBot). Те виждат вашите анализи, генетика и хранене и дават персонализирани съвети.",
    descEn: "The AI Chat unites 4 specialists who see your analyses, genetics and nutrition and give personalized advice.",
    icon: "message",
    targetTestId: "chat-interface",
  },
  {
    id: "done",
    titleBg: "Готови сте!",
    titleEn: "You're all set!",
    descBg: "Колкото повече данни добавяте (лаб., хранене, сън), толкова по-точни стават AI препоръките. Можете да рестартирате ръководството от меню „Потребител“.",
    descEn: "The more data you add (labs, meals, sleep), the more accurate AI recommendations become. Restart this guide from the user menu.",
    icon: "check",
    action: null,
  },
];

export default function GuidedTour() {
  const { user, API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [show, setShow] = useState(false);
  const [step, setStep] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Show tour only for non-admin users who completed onboarding but haven't done the tour
    if (!user || dismissed) return;
    if (user.role === "admin") return; // admins already know the platform
    const tourDone = localStorage.getItem(`nutramilo_tour_${user._id || user.email}`);
    const hasProfile = user.profile?.completedOnboarding === true;
    if (hasProfile && !tourDone) {
      const timer = setTimeout(() => setShow(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [user, dismissed]);

  const dismiss = useCallback(() => {
    setShow(false);
    setDismissed(true);
    if (user) {
      localStorage.setItem(`nutramilo_tour_${user._id || user.email}`, "done");
    }
  }, [user]);

  const next = () => {
    if (step < GUIDE_STEPS.length - 1) setStep(s => s + 1);
    else dismiss();
  };

  const prev = () => {
    if (step > 0) setStep(s => s - 1);
  };

  const scrollToTarget = (testId) => {
    if (!testId) return;
    const el = document.querySelector(`[data-testid="${testId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-[#0EA5E9]", "ring-offset-2", "rounded-xl");
      setTimeout(() => el.classList.remove("ring-2", "ring-[#0EA5E9]", "ring-offset-2", "rounded-xl"), 3000);
    }
  };

  if (!show) return null;

  const current = GUIDE_STEPS[step];
  const title = bg ? current.titleBg : current.titleEn;
  const desc = bg ? current.descBg : current.descEn;
  const isCompleted = current.checkFn ? current.checkFn(user) : false;
  const progress = ((step + 1) / GUIDE_STEPS.length) * 100;

  return (
    <div className="fixed bottom-24 md:bottom-8 left-4 right-4 md:left-auto md:right-6 md:w-[400px] z-[60]" data-testid="guided-tour">
      <div className={`rounded-2xl shadow-2xl border overflow-hidden ${isDark ? 'bg-[hsl(222,47%,11%)] border-[hsl(223,47%,20%)]' : 'bg-white border-gray-200'}`}>
        {/* Progress bar */}
        <div className="h-1 bg-gray-200">
          <div className="h-full bg-gradient-to-r from-[#0EA5E9] to-[#6366F1] transition-all duration-300" style={{ width: `${progress}%` }} />
        </div>

        <div className="p-4">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#0EA5E9] to-[#6366F1] flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`}>{title}</h3>
                <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {step + 1} / {GUIDE_STEPS.length}
                </p>
              </div>
            </div>
            <button onClick={dismiss} className={`p-1 rounded-lg ${isDark ? 'hover:bg-gray-800 text-gray-500' : 'hover:bg-gray-100 text-gray-400'}`}>
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <p className={`text-xs leading-relaxed mb-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{desc}</p>

          {/* Completion check */}
          {current.checkFn && (
            <div className={`flex items-center gap-1.5 text-xs mb-3 px-2 py-1.5 rounded-lg ${isCompleted ? (isDark ? 'bg-green-900/20 text-green-400' : 'bg-green-50 text-green-600') : (isDark ? 'bg-amber-900/20 text-amber-400' : 'bg-amber-50 text-amber-600')}`}>
              <CheckCircle2 className={`w-3.5 h-3.5 ${isCompleted ? '' : 'opacity-40'}`} />
              {isCompleted ? (bg ? "Завършено" : "Completed") : (bg ? "Все още не е направено" : "Not done yet")}
            </div>
          )}

          {/* Highlight target */}
          {current.targetTestId && (
            <button onClick={() => scrollToTarget(current.targetTestId)}
              className={`text-xs mb-3 px-2 py-1 rounded-lg font-medium transition-colors ${isDark ? 'bg-[#0EA5E9]/10 text-[#0EA5E9] hover:bg-[#0EA5E9]/20' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}>
              {bg ? "Покажи ми" : "Show me"} →
            </button>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={prev} disabled={step === 0} className="text-xs h-8">
              <ChevronLeft className="w-3 h-3 mr-0.5" /> {bg ? "Назад" : "Back"}
            </Button>
            <div className="flex gap-1">
              {GUIDE_STEPS.map((gs, i) => (
                <div key={gs.id} className={`w-1.5 h-1.5 rounded-full transition-colors cursor-pointer ${i === step ? 'bg-[#0EA5E9]' : i < step ? 'bg-[#0EA5E9]/40' : isDark ? 'bg-gray-700' : 'bg-gray-200'}`}
                  onClick={() => setStep(i)} />
              ))}
            </div>
            <Button size="sm" onClick={next} className="text-xs h-8 gradient-primary text-white">
              {step === GUIDE_STEPS.length - 1 ? (bg ? "Готово" : "Done") : (bg ? "Напред" : "Next")} <ChevronRight className="w-3 h-3 ml-0.5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// "Ръководство" entry in the profile dropdown — opens the static user guide
// page at /guide. Previous version replayed the dashboard tour, which broke
// user expectations ("link doesn't go anywhere"). Tour replay is still
// available from the GuidePage itself.
export function RestartTourButton({ user }) {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const bg = language === "bg";

  return (
    <button
      onClick={() => navigate("/guide")}
      className="flex items-center gap-2 w-full text-left"
      data-testid="profile-guide-link"
    >
      <Sparkles className="w-4 h-4" />
      {bg ? "Ръководство" : "User Guide"}
    </button>
  );
}
